import { useCallback, useEffect, useState } from "react";
import { EVENTS, STATUS } from "react-joyride";
import { hasSeenTour, markTourSeen, onTourSeen } from "./tourStorage";

// Drops any step whose target isn't currently in the DOM, so callers don't
// need to hand-filter steps around conditional UI (Approve button, Documents
// tab, etc.) — the tour just adapts to whatever is actually rendered.
function resolveVisibleSteps(steps) {
  return steps.filter((step) => document.querySelector(step.target));
}

export function useOnboardingTour(tourKey, steps, { ready = true, waitFor } = {}) {
  const [run, setRun] = useState(false);
  const [liveSteps, setLiveSteps] = useState([]);

  const tryStart = useCallback(() => {
    if (!ready || run || hasSeenTour(tourKey)) return;
    if (waitFor && !hasSeenTour(waitFor)) return;

    const visibleSteps = resolveVisibleSteps(steps);
    if (visibleSteps.length === 0) return;

    setLiveSteps(visibleSteps);
    setRun(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, tourKey, waitFor, run]);

  useEffect(() => {
    tryStart();
  }, [tryStart]);

  // If blocked on another tour, retry the instant it finishes instead of
  // waiting for this component's own props to change.
  useEffect(() => {
    if (!waitFor) return undefined;
    return onTourSeen((seenKey) => {
      if (seenKey === waitFor) tryStart();
    });
  }, [waitFor, tryStart]);

  const onEvent = useCallback(
    (data) => {
      const tourEnded =
        data.type === EVENTS.TOUR_END &&
        (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED);
      if (tourEnded) {
        markTourSeen(tourKey);
        setRun(false);
      }
    },
    [tourKey]
  );

  return { run, steps: liveSteps, onEvent };
}
